import {
  memo,
  useCallback,
  useMemo,
  useState,
  Fragment,
  useContext,
} from "react";

import {
  Box,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";

import PropTypes from "prop-types";

import { ButtonV1, ModalBox } from "components";

import FGDoCreatedCard from "./FGDoCreatedCard";
import FGPaymentSummaryCard from "./FGPaymentSummaryCard";
import FGDoPendingActions from "./FGDoPendingActions";
import FGEditableView from "./FGEditableView";
import FGUpdate from "./FGUpdate";
import FGHistory from "./FGHistory";

import FGRefreshContext from "../FGRefreshContext";

import {
  FG_TRACKING_LABELS,
  FG_LINE_ITEM_ENDPOINTS,
  DO_PENDING_AND_DO_CREATED_LINEITEM_COLUMNS,
  PAYMENT_PENDING_LINEITEM_COLUMNS,
} from "../constants";

import { postCall_v2, putCall_v2 } from "services";
import { getFgDueByTone, getFgToneColor, getJoinedText } from "../makeData";
import { useToaster } from "hooks";

import { getDateComparison } from "utils";

import { error, success, JOMS_API_BASE_V1_URL } from "constants";

const cellFlexColumnSx = { display: "flex", flexDirection: "column" };
const fgQtyBoxSx = { display: "flex", gap: 1, marginTop: 1 };
const refCellSx = { display: "flex", flexDirection: "column", paddingTop: 2 };

const FGOrderProcessingLineItemTable = ({ order }) => {
  const triggerToaster = useToaster();
  const onRefresh = useContext(FGRefreshContext);
  const [openFgUpdates, setOpenFgUpdates] = useState([]);
  const [fgUpdateData, setFgUpdateData] = useState({});
  const [historyOpenForLineItem, setHistoryOpenForLineItem] = useState(null);
  const openFgUpdateIds = useMemo(
    () => new Set(openFgUpdates),
    [openFgUpdates],
  );

  const visibleLineColumns = useMemo(() => {
    if (order?.tab === "paymentPending") {
      return PAYMENT_PENDING_LINEITEM_COLUMNS.filter(Boolean);
    }

    return DO_PENDING_AND_DO_CREATED_LINEITEM_COLUMNS.filter(Boolean);
  }, [order?.tab]);

  const historyLineItem = useMemo(
    () =>
      order.lineItems.find(
        (lineItem) => lineItem.lineItemId === historyOpenForLineItem,
      ),
    [historyOpenForLineItem, order.lineItems],
  );

  const closeFgUpdate = useCallback((lineItemId) => {
    setOpenFgUpdates((prev) => prev.filter((id) => id !== lineItemId));
    setFgUpdateData((prev) => {
      const updated = { ...prev };
      delete updated[lineItemId];
      return updated;
    });
  }, []);

  const handleCloseHistory = useCallback(() => {
    setHistoryOpenForLineItem(null);
  }, []);

  const handleToggleFgUpdate = useCallback(
    async (lineItem) => {
      const isOpen = openFgUpdateIds.has(lineItem.lineItemId);

      if (isOpen) {
        closeFgUpdate(lineItem.lineItemId);
        return;
      }

      try {
        const res = await postCall_v2(FG_LINE_ITEM_ENDPOINTS.fetchUpdates, {
          orderId: order.orderId,
          sellerOrderId: lineItem.sellerOrderId,
          lineitemId: lineItem.lineItemId,
        });
        setFgUpdateData((prev) => ({
          ...prev,
          [lineItem.lineItemId]: res.data.data,
        }));
        setOpenFgUpdates((prev) => [...prev, lineItem.lineItemId]);
      } catch {
        triggerToaster("Failed to fetch FG details", error);
      }
    },
    [closeFgUpdate, openFgUpdateIds, order.orderId, triggerToaster],
  );

  const updateFgReady = useCallback(
    async (payload) => {
      try {
        await putCall_v2(FG_LINE_ITEM_ENDPOINTS.update, payload);
        triggerToaster("FG updated successfully", success);
        onRefresh();
        if (payload?.actionType === "LINE_ITEM_UPDATE") {
          closeFgUpdate(payload?.updates[0]?.lineItemId);
        }
      } catch (err) {
        triggerToaster(err?.data?.errorMessage || "Failed to update FG", error);
      }
    },
    [closeFgUpdate, onRefresh, triggerToaster],
  );

  const handleFGUpdateData = useCallback(
    async (data, lineItem) => {
      const payload = {
        orderId: order.orderId,
        actionType: "LINE_ITEM_UPDATE",
        updates: [
          {
            lineItemId: lineItem.lineItemId,
            reason: data.reason || null,
            fgReady: {
              quantity: Number(data.fgReadyNow),
            },
            expectedFgData: data.fgBatches
              .filter((batch) => !batch.isReadOnly)
              .map((batch) => ({
                id: batch.id || null,
                quantity: Number(batch.qty),
                readinessDate: batch.readinessDate
                  ? new Date(batch.readinessDate).getTime()
                  : null,
                sellerOrderRef: batch.sellerOrderRef || null,
                sellerOlRef: batch.sellerOLRef || null,
              })),
          },
        ],
      };

      await updateFgReady(payload);
    },
    [order.orderId, updateFgReady],
  );

  const handleEditDoQty = useCallback(
    async (value, lineItemId, doId) => {
      try {
        const payload = {
          preDoId: doId,
          doUpdateRequest: [
            {
              lineItemId: lineItemId,
              updatedQty: +value,
            },
          ],
        };
        await postCall_v2(
          `${JOMS_API_BASE_V1_URL}/v1/inventory-data/edit-fg-do-quantity`,
          payload,
        );
        triggerToaster("Lineitem Do quantity updated successfully", success);
      } catch (err) {
        triggerToaster(
          err?.data?.errorMessage || "Please try again later",
          error,
        );
      }
    },
    [triggerToaster],
  );

  const handleMoveExpectedToReady = useCallback(
    async (lineItem) => {
      try {
        const payload = {
          doId: lineItem.psId,
          lineItemId: lineItem.lineItemId,
        };
        await postCall_v2(
          `/joms/api/internal/v1/finished-goods/mark-fg-ready`,
          payload,
        );
        triggerToaster("Expected Qty has been moved to FG Ready Qty.", success);
        onRefresh();
      } catch (err) {
        triggerToaster(
          err?.data?.errorMessage || "Please try again later",
          error,
        );
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [onRefresh, order.psId, triggerToaster],
  );

  const groupedByDoId = useMemo(() => {
    if (order.tab !== "doCreated") return [];

    const groups = {};
    order.lineItems.forEach((lineItem) => {
      const key = lineItem.doId || "-";
      if (!groups[key]) {
        groups[key] = {
          doId: lineItem.doId,
          doCreatedOn: lineItem.doCreatedOn,
          doReleasedOn: lineItem.doReleasedOn,
          doCancelledOn: lineItem.doCancelledOn,
          transactionStatus: lineItem.transactionStatus,
          doCreatedBy: lineItem.doCreatedBy,
          doLastModifiedBy: lineItem.doLastModifiedBy,
          lineItems: [],
        };
      }
      groups[key].lineItems.push(lineItem);
    });

    return Object.values(groups);
  }, [order.tab, order.lineItems]);

  const renderTableHeader = () => (
    <TableHead>
      <TableRow>
        {visibleLineColumns.map((column) => (
          <TableCell key={column}>
            <Typography variant="caption" color="text.secondary">
              {column}
            </Typography>
          </TableCell>
        ))}
      </TableRow>
    </TableHead>
  );

  const renderLineItemRow = (lineItem) => {
    const fgDueByTone = getFgDueByTone(lineItem);
    const isOverdue = getDateComparison(lineItem.fgDueBy) === -1;

    const materialText = lineItem?.materialShortDescription;
    const referenceText = getJoinedText([
      lineItem?.sellerOlRefs,
      lineItem?.soNumbers,
    ]);

    return (
      <Fragment key={lineItem.id}>
        <TableRow hover>
          <TableCell sx={cellFlexColumnSx}>
            <Typography variant="body2" fontWeight={600}>
              {lineItem.id}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {materialText}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {lineItem.description}
            </Typography>
          </TableCell>
          <TableCell>
            <Typography variant="body2" fontWeight={700}>
              {lineItem.orderQty}
            </Typography>
          </TableCell>
          <TableCell>
            <Typography variant="body2" fontWeight={700}>
              {lineItem.fgQty}
            </Typography>

            <Box sx={fgQtyBoxSx}>
              <Typography
                variant="body2"
                fontWeight={600}
                sx={{ color: "success.dark", fontSize: "11px" }}
              >
                {`${lineItem.fgReady} Ready`}
              </Typography>
              <Typography
                variant="body2"
                fontWeight={600}
                sx={{ color: "warning.dark", fontSize: "11px" }}
              >
                {`${lineItem.expectedFg} Exp`}
              </Typography>
            </Box>
          </TableCell>
          {order.tab !== "paymentPending" && (
            <TableCell>
              <FGEditableView
                lineItemData={lineItem}
                doId={order.preDoId}
                onEditDoQty={handleEditDoQty}
                isApproveRejectRefreshAction={order.tab === "doCreated"}
              />
            </TableCell>
          )}

          <TableCell sx={refCellSx}>
            <Typography
              variant="body2"
              fontWeight={500}
              sx={{ color: "info.main" }}
            >
              {lineItem.sellerOrderRef}
            </Typography>
            {!!referenceText && (
              <Typography variant="caption" color="text.secondary">
                {referenceText}
              </Typography>
            )}
            {!!lineItem?.sku && (
              <Typography variant="caption" color="text.primary">
                {lineItem?.sku}
              </Typography>
            )}
            {!!lineItem.description && (
              <Typography variant="caption" color="text.secondary">
                {lineItem.description}
              </Typography>
            )}
          </TableCell>
          {!!lineItem.rateExclTax && (
            <TableCell>
              <Typography variant="body2" fontWeight={700}>
                {lineItem.rateExclTax}
              </Typography>
            </TableCell>
          )}
          <TableCell>
            <Typography
              variant="body2"
              fontWeight={700}
              sx={(theme) => ({
                color: getFgToneColor(fgDueByTone, theme),
              })}
            >
              {lineItem.fgDueBy}
            </Typography>
            {isOverdue && (
              <Typography
                variant="body2"
                fontWeight={500}
                sx={{ color: "error.dark", whiteSpace: "nowrap" }}
              >
                Overdue <span>⚠</span>
              </Typography>
            )}
            {!isOverdue &&
              lineItem.fgDueBy !== "-" &&
              lineItem.orderQtyValue ===
                lineItem.fgReadyQtyValue + lineItem.expectedFgValue &&
              order?.tab === "doCreated" && (
                <Typography
                  variant="body2"
                  fontWeight={500}
                  sx={{ color: "success.dark", whiteSpace: "nowrap" }}
                >
                  ✓ Confirmed
                </Typography>
              )}
          </TableCell>
          {order?.tab !== "paymentPending" && order?.tab !== "doCreated" && (
            <TableCell>
              <Stack direction="row" spacing={1} alignItems="center">
                <Typography
                  color="primary"
                  variant="body2"
                  onClick={() => setHistoryOpenForLineItem(lineItem.lineItemId)}
                  sx={{ cursor: "pointer" }}
                >
                  {FG_TRACKING_LABELS.historyCta}
                </Typography>
                {/* {!isUpdateFgHidden && (
                              <ButtonV1
                                title={FG_TRACKING_LABELS.updateFgCta}
                                variant="outlined"
                                color="secondary"
                                size="small"
                                style={{
                                  minWidth: 72,
                                  height: 26,
                                  marginLeft: 2,
                                }}
                                textStyle={{ fontSize: 12 }}
                                onClick={() => handleToggleFgUpdate(lineItem)}
                              />
                            )} */}
              </Stack>
            </TableCell>
          )}
          {order?.tab === "doCreated" && (
            <TableCell>
              <ButtonV1
                title="Move Expected → FG Ready"
                size="small"
                textStyle={{ fontSize: 12 }}
                disabled={
                  lineItem.transactionStatus === "Rejected" ||
                  +lineItem.expectedFgValue === 0 ||
                  order.orderType === "PLANT SUPPLY"
                }
                onClick={() => handleMoveExpectedToReady(lineItem)}
              />
            </TableCell>
          )}
        </TableRow>
        {openFgUpdateIds.has(lineItem.lineItemId) && (
          <TableRow>
            <TableCell colSpan={visibleLineColumns.length}>
              <FGUpdate
                fgUpdateData={fgUpdateData[lineItem.lineItemId]}
                lineItem={lineItem}
                onSubmit={(data) => handleFGUpdateData(data, lineItem)}
                handleToggleFgUpdate={handleToggleFgUpdate}
                orderType={order.orderType}
              />
            </TableCell>
          </TableRow>
        )}
      </Fragment>
    );
  };

  return (
    <Box>
      {order.tab === "paymentPending" && <FGPaymentSummaryCard order={order} />}
      {order.tab === "doPending" && <FGDoPendingActions order={order} />}
      {order.tab === "doCreated" ? (
        groupedByDoId.map((doGroup) => (
          <Box key={doGroup.doId} sx={{ marginBottom: 4 }}>
            <FGDoCreatedCard doGroup={doGroup} />
            <TableContainer>
              <Table size="small">
                {renderTableHeader()}
                <TableBody>
                  {doGroup.lineItems.map((lineItem) =>
                    renderLineItemRow(lineItem),
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        ))
      ) : (
        <TableContainer>
          <Table size="small">
            {renderTableHeader()}
            <TableBody>
              {order.lineItems.map((lineItem) => renderLineItemRow(lineItem))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
      <ModalBox
        open={Boolean(historyLineItem)}
        width="40%"
        onCloseModal={handleCloseHistory}
      >
        {!!historyLineItem && (
          <FGHistory
            materialId={historyLineItem.id}
            materialDescription={historyLineItem.description}
            orderedQty={historyLineItem.orderQty}
            orderId={order.orderId}
            lineItemId={historyLineItem.lineItemId}
          />
        )}
      </ModalBox>
    </Box>
  );
};

FGOrderProcessingLineItemTable.propTypes = {
  order: PropTypes.shape({
    orderId: PropTypes.string.isRequired,
    lineItems: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.string.isRequired,
        description: PropTypes.string.isRequired,
        orderQty: PropTypes.string.isRequired,
        fgReady: PropTypes.string.isRequired,
        expectedFg: PropTypes.string.isRequired,
        fgAwaited: PropTypes.string.isRequired,
        fgDueBy: PropTypes.string.isRequired,
        eligible: PropTypes.bool.isRequired,
        invoicedQty: PropTypes.string,
        notInvoicedQty: PropTypes.string,
        shortCloseQty: PropTypes.string,
        orderConfirmedAt: PropTypes.string,
        hasPayment: PropTypes.bool,
        doCreatedQtyMt: PropTypes.number,
      }),
    ).isRequired,
  }).isRequired,
};

export default memo(FGOrderProcessingLineItemTable);
